import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import {
  Target,
  Calendar,
  Barbell,
  Books,
  ArrowsClockwise,
  FloppyDisk,
  Sparkle,
  TrendUp,
  ArrowRight,
  CheckCircle,
  Lightning,
  ShieldCheck,
} from "@phosphor-icons/react";

const STEPS = [
  {
    id: "step-1",
    n: "01",
    icon: Target,
    title: "Define the Mission",
    body: "Every plan starts with a goal. Pick a primary domain — Strength, Endurance, Mobility, or ACFT prep — and a measurable target (e.g., \"add 50 lb to deadlift\", \"run sub-14 2MR\", \"score 540 on ACFT\"). The clearer the mission, the sharper the plan.",
    tip: "Mesocycle = 4–12 weeks. Microcycle = 1 week of training. FM 7-22 stacks microcycles into mesocycles, and mesocycles into a macrocycle.",
  },
  {
    id: "step-2",
    n: "02",
    icon: Calendar,
    title: "Set the Duration",
    body: "In Plan Builder, decide how many weeks your mesocycle runs. Most operators do 4-week blocks: weeks 1-3 progressive load, week 4 deload. For ACFT prep, 8-12 weeks is the sweet spot.",
    tip: "Click \"+ Add Week\" to extend, or duplicate a week as a starting template for the next one.",
  },
  {
    id: "step-3",
    n: "03",
    icon: Barbell,
    title: "Architect the Week",
    body: "Each microcycle has 3-6 training days. Assign each day a focus: Push, Pull, Lower, Endurance, ACFT, Mobility/Recovery. Aim for a balanced split — don't stack two heavy lower-body days back-to-back.",
    tip: "FM 7-22 recommends alternating high-CNS sessions (heavy lifts, sprints) with lower-CNS work (mobility, easy aerobic).",
  },
  {
    id: "step-4",
    n: "04",
    icon: Books,
    title: "Pick Your Exercises",
    body: "Tap \"+ Add Exercise\" inside any day. Use the search bar, the domain pills (Strength / Endurance / Mobility / ACFT), and the quick-filter chips (No Equipment, Barbell, DB, KB, Core, Cardio, Power, Posterior Chain) to cut through 200+ drills.",
    tip: "Every exercise has a Beginner / Intermediate / Advanced tag and a list of alternates. If a drill is too hard or you're missing equipment, click the exercise to see its alternates.",
  },
  {
    id: "step-5",
    n: "05",
    icon: ArrowsClockwise,
    title: "Match the Skill Tier",
    body: "If \"Pistol Squat (Advanced)\" is your goal but you're not there yet, swap it for \"Bulgarian Split Squat (Intermediate)\" or \"Bodyweight Squat (Beginner)\" — the alternates are pre-curated for skill and equipment progression.",
    tip: "Progress tiers in order: bodyweight → kettlebell/DB → barbell. Build the pattern before chasing the load.",
  },
  {
    id: "step-6",
    n: "06",
    icon: Lightning,
    title: "Prescribe Sets & Reps",
    body: "Strength drills accept Sets × Reps × Weight. Endurance drills swap to Time per mile + Distance (e.g., \"8:30/mi · 3 mi\"). Mobility / ACFT use a single time-or-rep target. Inputs adapt automatically per domain.",
    tip: "Strength rep ranges: 3-5 for max-strength, 6-10 for hypertrophy, 12-20 for endurance. Endurance: keep ~80% of weekly mileage easy.",
  },
  {
    id: "step-7",
    n: "07",
    icon: FloppyDisk,
    title: "Save & Iterate",
    body: "Hit \"Save\" — your plan lives in /dashboard. Drag-and-drop to reorder exercises. Mark days complete to build a streak. Export to PDF for your training partner, or toggle \"Share\" to publish a public URL the community can copy.",
    tip: "Saved plans show a streak counter on your dashboard. Consistency beats novelty — same plan for 4-6 weeks is better than a new one each week.",
  },
  {
    id: "step-8",
    n: "08",
    icon: TrendUp,
    title: "Progress the Next Week",
    body: "Click \"Progress Next Week\" and the app applies FM 7-22 progressive overload automatically: +5% weight on strength, +2 reps on bodyweight, +10% distance on endurance. Don't bump everything at once — pick one variable per week.",
    tip: "Weeks 1-3: progress. Week 4: deload (drop volume by 40%, keep intensity). Repeat the cycle.",
  },
];

const QUICK_LINKS = [
  { to: "/ai", label: "Or skip ahead — let the AI Coach draft a plan", icon: Sparkle, testid: "tutorial-cta-ai" },
  { to: "/builder", label: "Open the Plan Builder and start now", icon: Barbell, testid: "tutorial-cta-builder" },
  { to: "/library", label: "Browse the 200+ exercise library", icon: Books, testid: "tutorial-cta-library" },
];

export default function Tutorial() {
  return (
    <Layout>
      {/* Hero */}
      <section className="mb-16">
        <div className="label-mono text-volt mb-2">// FIELD MANUAL // SECTION 1</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-6">
          BUILD A PROGRAM<br />
          <span className="text-volt">IN 8 STEPS.</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl text-base sm:text-lg">
          A field-tested walkthrough for designing your first training mesocycle inside Tactical Progression — from goal-setting through progressive overload. Skim it once, then build.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          {QUICK_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest font-display hover:border-volt hover:text-volt transition-colors"
            >
              <l.icon size={14} weight="bold" />
              {l.label}
              <ArrowRight size={14} weight="bold" />
            </Link>
          ))}
        </div>
      </section>

      {/* Concept primer */}
      <section className="mb-16 grid md:grid-cols-3 gap-px bg-border border border-border">
        {[
          {
            label: "MICROCYCLE",
            body: "A single week of training. The smallest planning unit in FM 7-22. Each microcycle balances stress and recovery across 3-6 training days.",
            icon: Calendar,
          },
          {
            label: "MESOCYCLE",
            body: "A block of 4-12 connected microcycles built around one objective (strength, hypertrophy, ACFT prep). Most blocks end with a deload week.",
            icon: ShieldCheck,
          },
          {
            label: "PROGRESSIVE OVERLOAD",
            body: "Adding small amounts of stress each week (load, reps, distance, density) so the body adapts without breaking. The engine of long-term gains.",
            icon: TrendUp,
          },
        ].map((c) => (
          <div key={c.label} className="bg-background p-6">
            <c.icon size={28} weight="bold" className="text-volt mb-4" />
            <div className="font-display text-xl font-black mb-2">{c.label}</div>
            <p className="text-sm text-muted-foreground tracking-wide">{c.body}</p>
          </div>
        ))}
      </section>

      {/* Steps */}
      <section className="mb-16">
        <div className="label-mono mb-4">// STEP-BY-STEP</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black mb-10">THE BUILD SEQUENCE</h2>

        <div className="space-y-px bg-border border border-border">
          {STEPS.map((s) => (
            <article
              key={s.id}
              id={s.id}
              data-testid={`tutorial-${s.id}`}
              className="bg-background p-6 sm:p-8 grid sm:grid-cols-[80px_56px_1fr] gap-4 sm:gap-6 items-start"
            >
              <div className="font-display font-black text-5xl sm:text-6xl text-volt leading-none tracking-tighter select-none">
                {s.n}
              </div>
              <div className="w-12 h-12 grid place-items-center border border-volt text-volt">
                <s.icon size={22} weight="bold" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-black uppercase mb-2">{s.title}</h3>
                <p className="text-muted-foreground tracking-wide mb-4 leading-relaxed">{s.body}</p>
                <div className="border-l-2 border-volt pl-4 py-1">
                  <div className="label-mono text-volt mb-1">// PRO TIP</div>
                  <p className="text-sm tracking-wide">{s.tip}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Sample week */}
      <section className="mb-16">
        <div className="label-mono mb-4">// EXAMPLE</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black mb-6">A 4-DAY ACFT MICROCYCLE</h2>
        <p className="text-muted-foreground max-w-3xl mb-8">
          Below is a single microcycle (one week) you can copy as a starting template. Open the Plan Builder and rebuild it in 5 minutes — then iterate.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              day: "DAY 1",
              focus: "Lower / Strength",
              items: ["Trap Bar Deadlift — 4×5", "Goblet Squat — 3×8", "DB Walking Lunge — 3×10/side", "Plank — 3×60s"],
            },
            {
              day: "DAY 2",
              focus: "Endurance",
              items: ["Tempo Run — 25 min @ 8:30/mi", "World's Greatest Stretch — 5/side", "Couch Stretch — 60s/side"],
            },
            {
              day: "DAY 3",
              focus: "Upper / ACFT",
              items: ["HRP Pyramid 1→10→1", "Pull-Up — 4×AMRAP", "DB Bench Press — 3×8", "SPT Throw — 3×5"],
            },
            {
              day: "DAY 4",
              focus: "Conditioning",
              items: ["Sprint-Drag-Carry — 5 rounds", "Farmer Carry — 4×40m", "Hanging Knee Raise — 3×10", "Mobility Flow — 10 min"],
            },
          ].map((d) => (
            <div key={d.day} className="tactical-card p-5">
              <div className="label-mono text-volt mb-1">// {d.focus.toUpperCase()}</div>
              <div className="font-display text-xl font-black mb-4">{d.day}</div>
              <ul className="space-y-2">
                {d.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm">
                    <CheckCircle size={14} weight="bold" className="text-volt mt-1 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-volt pt-12 pb-4">
        <div className="label-mono text-volt mb-2">// READY?</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black mb-6">YOUR FIRST PLAN BEATS YOUR PERFECT PLAN.</h2>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Go build something. You can always edit, copy, share, or progress the plan as you learn.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/builder"
            data-testid="tutorial-final-builder"
            className="inline-flex items-center gap-2 bg-volt text-black px-6 py-3 font-display uppercase tracking-widest font-bold hover:bg-white transition-colors"
          >
            <Barbell size={16} weight="bold" />
            Build Plan
            <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/ai"
            data-testid="tutorial-final-ai"
            className="inline-flex items-center gap-2 border border-volt text-volt px-6 py-3 font-display uppercase tracking-widest hover:bg-volt hover:text-black transition-colors"
          >
            <Sparkle size={16} weight="bold" />
            Or, draft with AI
          </Link>
        </div>
      </section>
    </Layout>
  );
}
